/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

'use client'

import type { SearchBoxAdministrativeUnitTypes } from '@mapbox/search-js-core'
import type { SearchBoxProps, SearchBoxRefType } from '@mapbox/search-js-react/dist/components/SearchBox'
import type { Theme } from '@mapbox/search-js-web'
import { forwardRef, useEffect, useRef, useState } from 'react'

import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

/** Default Mapbox Search JS theme aligned with shadcn semantic tokens (`globals.css`). */
export const SFMapBoxSearchBoxTheme = {
  cssText: `
      .Input {
          color: var(--foreground);
          caret-color: var(--foreground);
          min-height: 2.5rem;
      }
      .Input:focus {
          color: var(--foreground);
          caret-color: var(--foreground);
      }
  `,
  variables: {
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-md)',
    boxShadow: 'none',
    colorSecondary: 'var(--foreground)',
    colorText: 'var(--muted-foreground)',
    colorBackground: 'var(--input-mapbox)',
    colorBackgroundHover: 'var(--muted)',
  },
} satisfies Theme

export function mergeSFMapBoxSearchBoxTheme(overrides: Theme): Theme {
  return {
    ...SFMapBoxSearchBoxTheme,
    ...overrides,
    variables: {
      ...SFMapBoxSearchBoxTheme.variables,
      ...overrides.variables,
    },
  }
}

/** Matches shadcn `Input` (`dark:bg-input/30`). */
export const SFMapBoxSearchBoxCardInputTheme = mergeSFMapBoxSearchBoxTheme({
  variables: {
    colorBackground: 'var(--input-mapbox-card)',
    colorBackgroundHover: 'var(--muted)',
  },
})

export interface MapBoxSerachBoxProps extends Omit<SearchBoxProps, 'accessToken'> {
  accessToken: string
  /** When true, focusing the field requests the browser location for search proximity. */
  enableGeolocation?: boolean
  locationTypes?: string | Set<SearchBoxAdministrativeUnitTypes>
  placeholder?: string
  theme?: Theme
}

const MapBoxSearchBoxClient = forwardRef<SearchBoxRefType, MapBoxSerachBoxProps>((props, forwardedRef) => {
  const [currentLocation, setCurrentLocation] = useState<[number, number] | 'ip' | undefined>(undefined)
  const [SearchBox, setSearchBox] = useState<typeof import('@mapbox/search-js-react').SearchBox | null>(null)
  const searchBoxRef = useRef<HTMLDivElement>(null)
  const hasRequestedLocation = useRef(false)
  const { accessToken, enableGeolocation = true, locationTypes = 'place', placeholder, theme, ...rest } = props

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    import('@mapbox/search-js-react')
      .then((module) => {
        setSearchBox(() => module.SearchBox)
      })
      .catch((error) => {
        console.error('Failed to load Mapbox SearchBox:', error)
      })
  }, [])

  useEffect(() => {
    if (!enableGeolocation) {
      return
    }
    if (!SearchBox) {
      return
    }
    if (!searchBoxRef.current) {
      return
    }
    if (hasRequestedLocation.current) {
      return
    }

    const handleFocus = () => {
      if (hasRequestedLocation.current) {
        return
      }

      hasRequestedLocation.current = true

      if ('geolocation' in navigator) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            if (position.coords.longitude && position.coords.latitude) {
              setCurrentLocation([position.coords.longitude, position.coords.latitude])
            }
          },
          () => {
            setCurrentLocation('ip')
          }
        )
      }
    }

    const inputElement = searchBoxRef.current.querySelector('input')
    if (inputElement) {
      inputElement.addEventListener('focus', handleFocus, { once: true })
    }

    return () => {
      if (inputElement) {
        inputElement.removeEventListener('focus', handleFocus)
      }
    }
  }, [SearchBox, enableGeolocation])

  if (!SearchBox) {
    return <Skeleton className={cn('h-10 w-full border border-input bg-input/50 dark:bg-input/30')} />
  }

  return (
    <div ref={searchBoxRef}>
      <SearchBox
        accessToken={accessToken}
        options={{ types: locationTypes, proximity: currentLocation }}
        placeholder={placeholder || 'Enter the city where you live'}
        ref={forwardedRef}
        theme={theme || SFMapBoxSearchBoxTheme}
        {...rest}
      />
    </div>
  )
})

MapBoxSearchBoxClient.displayName = 'MapBoxSearchBoxClient'

export const MapBoxSearchBox = forwardRef<SearchBoxRefType, MapBoxSerachBoxProps>((props, forwardedRef) => {
  const [isClient, setIsClient] = useState(false)

  useEffect(() => {
    setIsClient(true)
  }, [])

  if (!isClient) {
    return <Skeleton className={cn('h-10 w-full border border-input bg-input/50 dark:bg-input/30')} />
  }

  return <MapBoxSearchBoxClient {...props} ref={forwardedRef} />
})

MapBoxSearchBox.displayName = 'MapBoxSearchBox'
